/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useEffect, useMemo, useState } from 'react';

import { ToDoOfferItem, ToDoOfferItemToDoStatusEnum } from 'client-code/recordings';

import { DeviceModel, Settings } from '../../../../core/device';
import { Channel } from '../../../../core/entities';
import { ErrorTypes } from '../../../../core/error';
import { NdvrInteractor, ServicesClientImpl } from '../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../core/locale';
import { Log } from '../../../../core/logging';
import {
    CursorManager,
    KeyManager,
    LocationUrls,
    ScreenControllerTypes,
    ScreenTaskControllerTypes,
} from '../../../../core/navigation';
import { TScreen } from '../../../../core/navigation/types';
import { NetworkManager } from '../../../../core/network/NetworkManager';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../core/tivo/tivo-redux-wrapper';
import { CcbSwitch } from '../../../../core/utils/ccb';
import { CcbSwitchName } from '../../../../core/utils/ccb/enums';
import { showEmptyToDoListScreen } from '../../../../store/screens/screens.reducers';
import {
    setToDoListApiErrorAction,
    setToDoListDataAction,
    setTotalCountToDoListAction,
} from '../../../../store/settings/todolist/todolist.actions';
import {
    disableGhostModeToDoList,
    resetToDoListState,
    unsubscribeFromUIOrchestratorToDoList,
} from '../../../../store/settings/todolist/todolist.reducers';
import { showWhisper } from '../../../../store/whisper/whisper.reducer';
import { AntiburnOsd, Mousetrap } from '../../../common';
import { useScreenController } from '../../../hooks';
import { NoNetworkOverlay } from '../../../NoNetworkOverlay';
import { WhisperTypes } from '../../../Whisper/WhisperTypes';
import { SettingsTemplate } from '../../SettingsTemplate';
import { Footer } from '../Footer/Footer';
import {
    getTodoItemsFromPage,
    LIST_ITEMS_MAX_COUNT,
    LIST_ROWS_ON_PAGE,
    mockConflictValues,
} from './ToDoList.utils';
import ToDoListContent from './ToDoListContent/ToDoListContent';
import ToDoListInteractiveLayer from './ToDoListInteractiveLayer/ToDoListInteractiveLayer';
import { TodoListPreview } from './TodoListPreview/TodoListPreview';

import './ToDoList.scss';

const TAG = 'ToDoListScreen';

const handleToDoListError = (dispatch: Function, error: unknown) => {
    Log.e(TAG, 'Error fetching todo list content:', error);
    const whisperMessage = Locale.getString(LocaleKeys.TODO_SCREEN_WHISPER_MESSAGE).replace(
        'ERROR_CODE',
        ErrorTypes.TODO_LIST_APIGW_5XX_C2302,
    );
    dispatch(
        showWhisper({
            message: whisperMessage,
            iconType: WhisperTypes.NO_ICON,
        }),
    );
    dispatch(setToDoListApiErrorAction(true));
    dispatch(resetToDoListState());
};

export const ToDoList = () => {
    const [isCursorActive, setIsCursorActive] = useState(false);
    const [allChannels, setAllChannels] = useState<Channel[]>([]);
    const settingsService = new Settings();
    const isVideoWindowEnabled = settingsService.isVideoWindowEnabled();

    const { isActive: isAntiBurnOsdActive } = useTivoSelector(
        (state: RootState) => state.antiburnOsd,
    );
    const { toDoItems, currentPage, totalCount, retryFetchCounter, verticalPosition, isError } =
        useTivoSelector((state: RootState) => state.todoList);

    const { actualItems } = getTodoItemsFromPage(toDoItems, currentPage);

    const itemHasConflict =
        (actualItems[verticalPosition] as ToDoOfferItem)?.toDoStatus ===
        ToDoOfferItemToDoStatusEnum.Conflict;

    const pageItems = useMemo(() => {
        const { items } = getTodoItemsFromPage(toDoItems, currentPage);
        return items;
    }, [toDoItems, currentPage]);

    const fetchAllChannels = async () => {
        const channelInteractor = new ServicesClientImpl();
        const channels = await channelInteractor.fetchChannels();

        setAllChannels(channels.channels);
    };

    useEffect(() => {
        fetchAllChannels();
    }, []);

    useScreenController<TScreen>({
        identifier: TAG,
        properties: {
            url: LocationUrls.TODO_LIST_SCREEN,
            screenController: ScreenControllerTypes.TODO_LIST_SCREEN,
            taskController: ScreenTaskControllerTypes.NO_TASK,
        },
    });

    const dispatch = useTivoDispatch();

    const onCursorStateChangeCallback = (state) => {
        setIsCursorActive(state);
        if (!isCursorActive) dispatch(disableGhostModeToDoList());
    };

    const onNetworkStateChangeCallback = () => {
        if (NetworkManager.isNetworkDisconnected()) {
            dispatch(resetToDoListState());
        } else {
            getToDoListInitialCall();
        }
    };

    useEffect(() => {
        const networkManagerRef = NetworkManager.subscribe(onNetworkStateChangeCallback);
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);

        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }

        // Cleanup any existing UIOrchestrator subscription when component mounts
        unsubscribeFromUIOrchestratorToDoList();

        return () => {
            CursorManager.unsubscribe(refCursorManager);
            NetworkManager.unsubscribe(networkManagerRef);
        };
    }, []);

    const fetchToDoList = async (offset: number, count: number) => {
        Log.i(TAG, 'Fetching to do list items content...');
        dispatch(setToDoListApiErrorAction(false));

        try {
            const tivoNetworkRequest = new NdvrInteractor().getToDoItems(offset, count);

            const response = await tivoNetworkRequest.send();

            if (!response) {
                Log.w(
                    TAG,
                    'No to do list items found for the given parameters.' +
                        `offset: ${offset}, count: ${count}`,
                );
            }
            const isCCBConflictMockEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
                CcbSwitchName.ENABLE_TODO_LIST_CONFLICT_MOCK_REMOVE_IN_KST_14803,
            );

            if (!isCCBConflictMockEnabled)
                return {
                    ...response,
                    isError: false,
                };

            return {
                ...response,
                toDoItems: mockConflictValues(response.toDoItems),
                isError: false,
            };
        } catch (error) {
            handleToDoListError(dispatch, error);
            return { toDoItems: [], totalCount: 0, isError: true };
        }
    };

    const getToDoListInitialCall = async () => {
        if (toDoItems.length > 0) {
            // When returning from program screen, we want to maintain the current list
            Log.i(TAG, 'To do list items already fetched.');
            return;
        }
        const response = await fetchToDoList(0, LIST_ROWS_ON_PAGE * 2);

        if ((!response || response.totalCount === 0) && !response.isError) {
            Log.i(TAG, 'No to do list items available.');
            dispatch(showEmptyToDoListScreen());
            return;
        }

        dispatch(setToDoListDataAction(response.toDoItems));
        dispatch(setTotalCountToDoListAction(response.totalCount));
    };

    const getToDoListNextPageCall = async () => {
        const response = await fetchToDoList(toDoItems.length, LIST_ROWS_ON_PAGE * 2);

        if (response?.toDoItems?.length) {
            const newToDoItems = [...toDoItems, ...response.toDoItems];
            dispatch(setToDoListDataAction(newToDoItems));
        }
    };

    useEffect(() => {
        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }

        getToDoListInitialCall();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isError && retryFetchCounter !== 0) {
            getToDoListInitialCall();
        }
    }, [retryFetchCounter]);

    useEffect(() => {
        if (currentPage === 0) return;

        const totalPagesLoaded = Math.ceil(toDoItems.length / LIST_ROWS_ON_PAGE);
        const shouldFetchNextPage =
            currentPage === totalPagesLoaded - 1 &&
            toDoItems.length < totalCount &&
            toDoItems.length < LIST_ITEMS_MAX_COUNT;
        if (shouldFetchNextPage) {
            getToDoListNextPageCall();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    return (
        <SettingsTemplate title={Locale.getString(LocaleKeys.TODO_LIST_PAGE_HEADER_TITLE)}>
            <div className="to-do-list-screen">
                <div className="todo-list-content-container">
                    <ToDoListContent
                        toDoItems={pageItems}
                        currentPage={currentPage}
                        allChannels={allChannels}
                    />

                    <div
                        className={`todo-list-preview-container ${!isVideoWindowEnabled ? 'video-window-off' : ''}`}
                    >
                        <Mousetrap value="todo-list-preview">
                            <div
                                className={`todo-list-preview-trap-height ${!isVideoWindowEnabled ? 'video-window-off' : ''}`}
                            >
                                <TodoListPreview />
                            </div>
                        </Mousetrap>
                    </div>
                </div>
                {isAntiBurnOsdActive && <AntiburnOsd />}
                <Footer shouldShowRightMessage={itemHasConflict} />
                {DeviceModel.isWebOsPlatform() && CursorManager.isCursorAppeared() ? (
                    <ToDoListInteractiveLayer />
                ) : null}
                <NoNetworkOverlay />
            </div>
        </SettingsTemplate>
    );
};

export default ToDoList;
